/**
 * useExposureFilter - Groups orders and positions by game, cross-referenced
 * against live market data. Returns only games where the user has exposure
 * (open orders or positions).
 */

import { useMemo } from 'react';
import type { Position, KalshiOrder } from '../../types';
import type { ConsolidatedGameBooks } from '../../lib/nbaConsolidated/types';
import type {
  GameData,
  MoneylineGameData,
  SpreadGameData,
  TotalGameData,
} from '../../lib/sportsStream/types';
import { parseKalshiTicker } from '../../lib/kalshiTicker';

export interface GameExposure {
  /** Stable key for this game (e.g. "2026-02-23|LAL|BOS") */
  gameKey: string;
  awayCode: string;
  homeCode: string;
  awayName?: string;
  homeName?: string;
  date: string;
  startTimePt?: string | null;

  /** All tickers associated with this game */
  tickers: Set<string>;

  /** User's orders in this game */
  orders: KalshiOrder[];
  /** User's positions in this game */
  positions: Position[];

  /** Moneyline data from consolidated games (if available) */
  moneyline: ConsolidatedGameBooks | null;
  /** Spread data from sports stream (if available) */
  spread: SpreadGameData | null;
  /** Total data from sports stream (if available) */
  total: TotalGameData | null;
}

export interface UseExposureFilterParams {
  orders: KalshiOrder[];
  positions: Position[];
  positionGameDateByTicker: Record<string, string>;
  consolidatedGames?: ConsolidatedGameBooks[];
  sportsGames?: Map<string, GameData[]>;
}

/**
 * Extract team codes and date from a Kalshi ticker using the full parser.
 * Handles all formats: KXNBAGAME-26FEB04BOSMIA-BOS, KXNFLSPREAD-..., etc.
 */
export function extractGameInfoFromTicker(
  ticker: string
): { away: string; home: string; date: string | null } | null {
  const parsed = parseKalshiTicker(ticker);
  if (parsed.game) {
    return { away: parsed.game.awayCode, home: parsed.game.homeCode, date: parsed.date };
  }
  return null;
}

/**
 * Build a lookup from ticker -> game key using all available market data.
 */
export function buildTickerToGameKey(
  consolidatedGames: ConsolidatedGameBooks[],
  sportsGames: Map<string, GameData[]>
): Map<string, string> {
  const map = new Map<string, string>();

  for (const game of consolidatedGames) {
    const key = game.key;
    if (game.kalshi?.markets.away.marketTicker) {
      map.set(game.kalshi.markets.away.marketTicker, key);
    }
    if (game.kalshi?.markets.home.marketTicker) {
      map.set(game.kalshi.markets.home.marketTicker, key);
    }
    if (game.polymarket?.markets.away.marketTicker) {
      map.set(game.polymarket.markets.away.marketTicker, key);
    }
    if (game.polymarket?.markets.home.marketTicker) {
      map.set(game.polymarket.markets.home.marketTicker, key);
    }
  }

  for (const [, games] of sportsGames) {
    for (const game of games) {
      const key = `${game.date}|${game.awayCode.toUpperCase()}|${game.homeCode.toUpperCase()}`;
      if (game.marketType === 'moneyline') {
        const ml = game as MoneylineGameData;
        if (ml.away?.marketTicker) map.set(ml.away.marketTicker, key);
        if (ml.home?.marketTicker) map.set(ml.home.marketTicker, key);
      } else if (game.marketType === 'spread') {
        const sp = game as SpreadGameData;
        for (const [, info] of sp.markets) {
          if (info.book.marketTicker) map.set(info.book.marketTicker, key);
        }
      } else if (game.marketType === 'total') {
        const tot = game as TotalGameData;
        for (const [, book] of tot.markets) {
          if (book.marketTicker) map.set(book.marketTicker, key);
        }
      }
    }
  }

  return map;
}

/**
 * Pure computation: groups orders and positions by game, cross-referenced
 * against live market data. Returns only games where the user has exposure.
 */
export function computeGameExposures({
  orders,
  positions,
  positionGameDateByTicker,
  consolidatedGames = [],
  sportsGames = new Map(),
}: UseExposureFilterParams): GameExposure[] {
  const tickerToGameKey = buildTickerToGameKey(consolidatedGames, sportsGames);

  const consolidatedByKey = new Map<string, ConsolidatedGameBooks>();
  for (const g of consolidatedGames) {
    consolidatedByKey.set(g.key, g);
  }

  // Index sports games by game key + market type
  const spreadByKey = new Map<string, SpreadGameData>();
  const totalByKey = new Map<string, TotalGameData>();
  for (const [, games] of sportsGames) {
    for (const game of games) {
      const key = `${game.date}|${game.awayCode.toUpperCase()}|${game.homeCode.toUpperCase()}`;
      if (game.marketType === 'spread') spreadByKey.set(key, game as SpreadGameData);
      if (game.marketType === 'total') totalByKey.set(key, game as TotalGameData);
    }
  }

  // Group orders and positions by game key
  const exposureMap = new Map<
    string,
    {
      orders: KalshiOrder[];
      positions: Position[];
      tickers: Set<string>;
    }
  >();

  const activeOrders = orders.filter((o) => o.status === 'resting' || o.status === 'pending');

  function ensureEntry(gameKey: string) {
    if (!exposureMap.has(gameKey)) {
      exposureMap.set(gameKey, { orders: [], positions: [], tickers: new Set() });
    }
    return exposureMap.get(gameKey)!;
  }

  for (const order of activeOrders) {
    let gameKey = tickerToGameKey.get(order.ticker);
    if (!gameKey) {
      const info = extractGameInfoFromTicker(order.ticker);
      const date = info?.date ?? positionGameDateByTicker[order.ticker];
      if (info && date) {
        gameKey = `${date}|${info.away}|${info.home}`;
      }
    }
    if (gameKey) {
      const entry = ensureEntry(gameKey);
      entry.orders.push(order);
      entry.tickers.add(order.ticker);
    }
  }

  for (const pos of positions) {
    if (pos.quantity === 0) continue;
    let gameKey = tickerToGameKey.get(pos.ticker);
    if (!gameKey) {
      const info = extractGameInfoFromTicker(pos.ticker);
      const date = info?.date ?? positionGameDateByTicker[pos.ticker];
      if (info && date) {
        gameKey = `${date}|${info.away}|${info.home}`;
      }
    }
    if (gameKey) {
      const entry = ensureEntry(gameKey);
      entry.positions.push(pos);
      entry.tickers.add(pos.ticker);
    }
  }

  // Build GameExposure objects
  const result: GameExposure[] = [];
  for (const [gameKey, entry] of exposureMap) {
    const parts = gameKey.split('|');
    if (parts.length < 3) continue;

    const [date, awayCode, homeCode] = parts as [string, string, string];

    const consolidated = consolidatedByKey.get(gameKey) ?? null;
    const spread = spreadByKey.get(gameKey) ?? null;
    const total = totalByKey.get(gameKey) ?? null;

    // Get display names from whichever source has them
    const awayName = consolidated?.awayName ?? spread?.awayName ?? total?.awayName ?? awayCode;
    const homeName = consolidated?.homeName ?? spread?.homeName ?? total?.homeName ?? homeCode;
    const startTimePt = consolidated?.startTimePt ?? spread?.startTimePt ?? total?.startTimePt;

    result.push({
      gameKey,
      awayCode,
      homeCode,
      awayName,
      homeName,
      date,
      startTimePt,
      tickers: entry.tickers,
      orders: entry.orders,
      positions: entry.positions,
      moneyline: consolidated,
      spread,
      total,
    });
  }

  // Sort by date, then game time
  result.sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return (a.startTimePt ?? '').localeCompare(b.startTimePt ?? '');
  });

  return result;
}

export function useExposureFilter(params: UseExposureFilterParams): GameExposure[] {
  return useMemo(
    () => computeGameExposures(params),
    [
      params.orders,
      params.positions,
      params.positionGameDateByTicker,
      params.consolidatedGames,
      params.sportsGames,
    ]
  );
}
